import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth, firebaseMessaging } from "@/helpers/firebase/firebase";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotaStore } from "@/stores/modules/quota/quota";
import { resetAllStoreData } from "@/stores/clear-all-stores";
import router from "@/router/router";
import { deleteToken } from "firebase/messaging";
import { logoutFlutterAuthInstance } from "@/helpers/flutter/features/auth";
import { useI18n } from "vue-i18n";
import {
  normalizeSupportedLanguage,
  syncUserPushLanguageForGroups,
} from "@/helpers/firestore/GroupPushSettingsService";
import { sendLanguageToFlutter } from "@/helpers/flutter/features/language";

/**
 * App store managing application initialization and state
 * Uses Pinia with Composition API and TypeScript
 */
export const useAppStore = defineStore("app", () => {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const groupsStore = useGroupsStore();
  const quotaStore = useQuotaStore();
  const { locale } = useI18n();

  /* State */
  const finalized = ref<boolean>(false);
  const initialError = ref<boolean>(false);
  const bootMessage = ref<string>("");

  /* Computed / Getters */
  const isReady = computed(() => finalized.value && !initialError.value);

  /* Actions */
  function setFinalized(value: boolean): void {
    finalized.value = value;
  }

  function setInitialError(value: boolean): void {
    initialError.value = value;
  }

  function setBootMessage(message: string): void {
    bootMessage.value = message;
  }

  /**
   * Wait for the app to be finalized before proceeding
   * Useful for router guards that need to wait for data to be loaded
   * @returns A promise that resolves when the app is finalized
   */
  function waitForFinalization(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (finalized.value) {
        resolve();
      } else {
        const unwatch = watch(finalized, (newValue) => {
          if (newValue) {
            unwatch();
            resolve();
          }
        });
      }
    });
  }

  /**
   * Sync language preference between localStorage and Firestore
   * This ensures language changes are persisted across sessions and devices
   *
   * Priority:
   * 1. If the user document has no language → persist localStorage or current app locale
   * 2. If localStorage is empty but Firestore has a value → sync to localStorage
   * 3. If localStorage has a value and Firestore differs → sync to Firestore
   */
  async function syncLanguagePreference(): Promise<void> {
    const localStorageLanguage = localStorage.getItem("locale");
    const localStorageSupportedLanguage = localStorageLanguage
      ? normalizeSupportedLanguage(localStorageLanguage)
      : undefined;
    const userLanguage = userStore.userData?.language;
    const userId = authStore.authUuid;
    const groupIds = userStore.userData?.groups || [];

    if (!userId || !userStore.userData) {
      return;
    }

    if (!userLanguage) {
      // Existing users may not have a Firestore language yet.
      // Persist the current app language so the user document always has one.
      const language = normalizeSupportedLanguage(
        localStorageSupportedLanguage || locale.value,
      );
      localStorage.setItem("locale", language);
      locale.value = language;
      await userStore.postUserLanguage(language);
    } else if (!localStorageSupportedLanguage) {
      // Case 1: User logged in after logout, restore language from Firestore
      console.log(
        `Restoring language preference from Firestore: ${userLanguage} → localStorage`,
      );
      localStorage.setItem("locale", userLanguage);
      // Update i18n locale
      locale.value = userLanguage;
      await syncUserPushLanguageForGroups({
        userId,
        groupIds,
        language: userLanguage,
      });
    } else if (localStorageSupportedLanguage !== userLanguage) {
      // Case 2: User changed language before login, sync to Firestore
      console.log(
        `Syncing language preference: ${localStorageSupportedLanguage} (localStorage) → Firestore (was: ${userLanguage})`,
      );
      localStorage.setItem("locale", localStorageSupportedLanguage);
      locale.value = localStorageSupportedLanguage;
      await userStore.postUserLanguage(localStorageSupportedLanguage);
    } else {
      // Case 3: Firestore/localStorage already match; backfill group push language
      if (localStorageLanguage !== userLanguage) {
        localStorage.setItem("locale", userLanguage);
      }
      locale.value = userLanguage;
      await syncUserPushLanguageForGroups({
        userId,
        groupIds,
        language: userLanguage,
      });
    }

    sendLanguageToFlutter(locale.value);
  }

  /**
   * Initialize the application by setting up Firebase auth listener
   * and fetching initial data when user is authenticated
   */
  async function initializeApp(): Promise<void> {
    try {
      // Check if we have cached data from localStorage
      const hasAuthUser = authStore.firebaseAuthUser;
      const hasUserData = userStore.userData;

      // If we have all cached data, we can show the app immediately
      // but still refresh in the background
      if (hasAuthUser && hasUserData) {
        setFinalized(true);
        setInitialError(false);
      }

      // Set up Firebase auth state listener
      onAuthStateChanged(firebaseAuth, async function (user) {
        try {
          if (!user) {
            // User logged out
            if (authStore.firebaseAuthUser) {
              if (firebaseMessaging) {
                try {
                  await deleteToken(firebaseMessaging);
                } catch {
                  //
                }
              }
              logoutFlutterAuthInstance();
              await resetAllStoreData();
              window.localStorage.clear();
              //Do not await, never resolves
              router.push({ name: "Login" });
            }
            setFinalized(true);
            setInitialError(false);
          } else {
            // User logged in
            authStore.setAuthUser(user);
            authStore.setAuthIdToken(await user.getIdTokenResult());

            // If we already have user data, just refresh in background
            if (userStore.userData) {
              setFinalized(true);
              setInitialError(false);
              // Refresh data in background
              userStore.refreshUserData().catch((error) => {
                console.error("Background refresh failed:", error);
                // If background refresh fails, set error state so user knows
                setInitialError(true);
              });
              groupsStore.refreshAllUserGroupsData().catch((error) => {
                console.error("Background group refresh failed:", error);
                // Group refresh failure is less critical, just log it
              });
              // Sync language preference from localStorage to Firestore if different
              syncLanguagePreference().catch((error) => {
                console.error("Language preference sync failed:", error);
              });
              // Fetch quota in background
              quotaStore.fetchQuota().catch(() => {});
            } else {
              // First load - fetch data before finalizing
              try {
                setBootMessage("boot.downloading_profile");
                await userStore.refreshUserData();
                setBootMessage("boot.downloading_group");
                await groupsStore.refreshAllUserGroupsData();

                // Check if there was a real error (not just missing user doc, which is valid)
                if (userStore.userFailed) {
                  throw new Error("Failed to load user data");
                }

                setFinalized(true);
                setInitialError(false);
                // Sync language preference from localStorage to Firestore if different
                // Only sync if we have user data
                if (userStore.userData) {
                  syncLanguagePreference().catch((error) => {
                    console.error("Language preference sync failed:", error);
                  });
                }
                // Fetch quota in background
                quotaStore.fetchQuota().catch(() => {});
              } catch (error) {
                console.error("Failed to load initial user data:", error);
                setInitialError(true);
                setFinalized(true);
              }
            }
          }
        } catch (error) {
          console.error("Error during auth state change:", error);
          setInitialError(true);
          setFinalized(true);
        }
      });
    } catch (error) {
      console.error("Error initializing app:", error);
      setInitialError(true);
      setFinalized(true);
    }
  }

  function reset(): void {
    finalized.value = false;
    initialError.value = false;
    bootMessage.value = "";
  }

  /* Exposed store */
  return {
    // State
    finalized,
    initialError,
    bootMessage,

    // Computed
    isReady,

    // Actions
    setFinalized,
    setInitialError,
    setBootMessage,
    waitForFinalization,
    initializeApp,
    reset,
  };
});
